import { RequestStatus } from 'typed/api';
import { Entity, NetworkArray } from 'typed/api/entities';
import { IAction } from './action';
import networkAction, { NetworkAction } from './networkAction';
import { EntityActionMap, IEntityActionCreatorSet, InferredActionType, PartialEntity, ProvidedKeys } from './entityActionSet';

export interface DoubleNestedEntityActionCreatorSet<T extends string, G extends Entity, P extends Entity, E extends Entity> extends IEntityActionCreatorSet<T, E> {
    loadAll(grandParentID: G['ID'], parentID: P['ID'], ...options: unknown[]): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; }, NetworkArray<E>>;
    load(grandParentID: G['ID'], parentID: P['ID'], entityID: E['ID'], ...options: unknown[]): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entityID: E['ID']; }, E>;
    addAll(grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[]): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    addAll(grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync: true): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, number[]>;
    add(grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    add(grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync: true, reloadAfterSync?: boolean): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, number>;
    editAll(grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync?: boolean): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entities: PartialEntity<E>[]; }, void>;
    edit(grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync?: boolean, reloadAfterSync?: boolean): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entity: PartialEntity<E>; }, void>;
    deleteAll(grandParentID: G['ID'], parentID: P['ID'], entities: E[]): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entityIDs: E['ID'][]; }, void>;
    delete(grandParentID: G['ID'], parentID: P['ID'], entity: E, sync?: boolean): NetworkAction<T, { grandParentID: G['ID']; parentID: P['ID']; entityID: E['ID']; }, void>;
    voidAll(grandParentID: G['ID'], parentID: P['ID'], immediately?: true): IAction<T, { grandParentID: G['ID']; parentID: P['ID']; immediately: boolean; }>;
    void(grandParentID: G['ID'], parentID: P['ID'], entityID: E['ID'], immediately?: true): IAction<T, { grandParentID: G['ID']; parentID: P['ID']; immediately: boolean; entityID: E['ID']; }>;
}

export type DoubleNestedEntityMappedActionCreators<G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>> =
    { readonly [K in ProvidedKeys<M>]: DoubleNestedEntityActionCreatorSet<InferredActionType<M, K>, G, P, E>[K] };

/**
 * @param grandParent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param parent a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
 * @param entity a dummy parameter used to properly guess entity type, should be removed once API methods have proper types
  */
export default function doubleNestedEntityNetworkActionSet<G extends Entity, P extends Entity, E extends Entity, M extends EntityActionMap<any, E>>(_grandParent: G, _parent: P, _entity: E, map: M): { readonly [K in ProvidedKeys<M>]: DoubleNestedEntityActionCreatorSet<InferredActionType<M, K>, G, P, E>[K] } {
    // The return type is actually MappedActionCreators<E, M>, but if result is declared this way, type hints/intellisense just show the name, which is not so helpful.
    // Explicit declaration here causes nice hints of all available methods, overloads etc.

    const
        addAllType = map.addAll && map.addAll.type!,
        addAllMethod = map.addAll && map.addAll.method!,
        addAllAction: (<T extends string, P>(grandParentID: G['ID'], parentID: number, entities: PartialEntity<E>[]) => NetworkAction<T, P, number[]>) | null = addAllType && addAllMethod ?
            (grandParentID, parentID, entities) => networkAction(
                addAllType, { grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities }, addAllMethod, entities
            ) :
            null,

        addType = map.add && map.add.type!,
        addMethod = map.add && map.add.method!,
        addAction: (<T extends string, P>(grandParentID: G['ID'], parentID: number, entity: Partial<E>) => NetworkAction<T, P, number>) | null = addType && addMethod ?
            (grandParentID, parentID, entity) => networkAction(
                addType, { grandParentID, parentID, entityID: entity.ID, entity }, addMethod, entity
            ) :
            null,


        result = {} as unknown as DoubleNestedEntityActionCreatorSet<string, G, P, E>;

    if (map.loadAll) {
        const
            type = map.loadAll.type!,
            method = map.loadAll.method!;

        result.loadAll = (grandParentID, parentID, ...options) => networkAction(type, { grandParentID, parentID }, method, ...options);
    }

    if (map.load) {
        const
            type = map.load.type!,
            method = map.load.method!;

        result.load = (grandParentID, parentID, entityID, ...options) => networkAction(
            type, { grandParentID, parentID, entityID }, method, entityID, ...options
        );
    }

    if (map.addAll) {
        if (!addAllType || !addAllAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).addAll = (grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof addAllType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addAllType,
                    payload: { grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entities.map(({ ID }) => ID);
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(addAllAction(grandParentID, parentID, entities)) as any;
                }
                else {
                    if (result.loadAll) {
                        await dispatch(addAllAction(grandParentID, parentID, entities));
                        return await dispatch(result.loadAll(grandParentID, parentID, ...reloadAllOptions));
                    }

                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }


    if (map.editAll) {
        const
            type = map.editAll.type!,
            method = map.editAll.method!,
            editAllAction = (grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[]) => networkAction(
                type, { grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities }, method, entities
            );

        (result as any).editAll = (grandParentID: G['ID'], parentID: P['ID'], entities: PartialEntity<E>[], sync = false, reloadAllAfterSync = false, ...reloadAllOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { grandParentID, parentID, entityIDs: entities.map(({ ID }) => ID), entities, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAllAfterSync || !DEBUG && !result.loadAll) {
                    return await dispatch(editAllAction(grandParentID, parentID, entities));
                }
                else {
                    if (result.loadAll) {
                        await dispatch(editAllAction(grandParentID, parentID, entities));
                        return await dispatch(result.loadAll(grandParentID, parentID, ...reloadAllOptions));
                    }


                    throw new Error('Can\'t reloadAll after sync - methodsMap.loadAll is not configured.');
                }
            };
    }

    if (map.add) {
        if (!addType || !addAction) {
            throw new Error('Can\'t happen.');
        }

        (result as any).add = (grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof addType, any, any> =>
            async(dispatch) => {
                dispatch({
                    type: addType,
                    payload: { grandParentID, parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                if (!sync) {
                    return entity.ID;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(addAction(grandParentID, parentID, entity));
                }
                else {
                    if (result.load) {
                        const newID = await dispatch(addAction(grandParentID, parentID, entity));
                        return await dispatch(result.load(grandParentID, parentID, newID!, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }

    if (map.edit) {
        const
            type = map.edit.type!,
            method = map.edit.method!,

            editAction = (grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>) => networkAction(
                type, { grandParentID, parentID, entityID: entity.ID, entity }, method, entity
            );

        (result as any).edit = (grandParentID: G['ID'], parentID: P['ID'], entity: PartialEntity<E>, sync = false, reloadAfterSync = false, ...reloadOptions: unknown[]): NetworkAction<typeof type, any, any> =>
            async(dispatch) => {
                dispatch({
                    type,
                    payload: { grandParentID, parentID, entityID: entity.ID, entity, status: RequestStatus.UNSYNCED }
                });

                const syncAction =
                    !entity.Custom || entity.Custom.status === RequestStatus.UNSYNCED ?
                        addAction as unknown as typeof editAction :
                        editAction;

                if (!sync) {
                    return undefined;
                }
                else if (!reloadAfterSync || !DEBUG && !result.load) {
                    return await dispatch(syncAction(grandParentID, parentID, entity));
                }
                else {
                    if (result.load) {
                        await dispatch(syncAction(grandParentID, parentID, entity));
                        return await dispatch(result.load(grandParentID, parentID, entity.ID, ...reloadOptions));
                    }

                    throw new Error('Can\'t reload after sync - methodsMap.load is not configured.');
                }
            };
    }


    if (map.deleteAll) {
        const
            type = map.deleteAll.type!,
            method = map.deleteAll.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.deleteAll = (grandParentID, parentID, entities) => async(dispatch) => {
            const
                localEntityIDs = entities.filter(({ Custom: { status } }) => status === RequestStatus.UNSYNCED).map(({ ID }) => ID),
                remoteEntityIDs = entities.filter(({ Custom: { status } }) => status !== RequestStatus.UNSYNCED).map(({ ID }) => ID);

            if (localEntityIDs.length) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, grandParentID, parentID, entityIDs: localEntityIDs, response: undefined }
                });
            }
            if (remoteEntityIDs.length) {
                await dispatch(networkAction(type, { grandParentID, parentID, entityIDs: remoteEntityIDs }, method, remoteEntityIDs));
            }
        };
    }


    if (map.delete) {
        const
            type = map.delete.type!,
            method = map.delete.method!;

        // TODO this code works if entity successfully synced or not synced at all
        // but it will fail to delete entity if sync request failed or not yet finished
        // latter situation is somewhat fixable by building request queue
        // but how to delete entity from DB if it has been added but its new ID has been lost in network failure?
        result.delete = (grandParentID, parentID, entity, sync = true) => async(dispatch) => {
            if (!sync || entity.Custom.status === RequestStatus.UNSYNCED) {
                dispatch({
                    type,
                    payload: { status: RequestStatus.SUCCEEDED, grandParentID, parentID, entityID: entity.ID, response: undefined }
                });
            }
            else {
                await dispatch(networkAction(type, { grandParentID, parentID, entityID: entity.ID }, method, entity.ID));
            }
        };
    }

    if (map.voidAll) {
        const { type } = map.voidAll;
        result.voidAll = (grandParentID, parentID, immediately = true) => ({
            type,
            payload: { grandParentID, parentID, immediately }
        });
    }

    if (map.void) {
        const { type } = map.void;
        result.void = (grandParentID, parentID, entityID, immediately = true) => ({
            type,
            payload: { grandParentID, parentID, entityID, immediately }
        });
    }

    return result as unknown as DoubleNestedEntityMappedActionCreators<G, P, E, M>;
}


